import { ConsumptionRow } from "../models/consumptionRow";
import { formatDate } from "@/hooks/metrics/useFormattedDate";
import { formatNumber } from "@/utilities/formatNumber";

/** The `t` this needs, narrowed so tests do not have to stand up i18next. */
export type Translate = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string;

/**
 * Puts what is incomplete about a row into sentences.
 *
 * Each one says what is missing and, where the row knows it, by how much — a warning triangle
 * that only says "incomplete" leaves the reader no way to judge whether the figure is still
 * usable, which is the whole question they opened the report to answer.
 *
 * @param row - The row to describe.
 * @param t - The translation function.
 * @param language - The language to format numbers and dates in.
 * @returns One sentence per issue, in the order the row lists them.
 */
export function energyIssueTexts(row: ConsumptionRow, t: Translate, language: string): string[] {
  return row.issues.map(issue => {
    switch (issue) {
      case "requestFailed":
        return t(
          "energyReport.issues.requestFailed",
          "Die Abfrage für dieses Fahrzeug ist fehlgeschlagen. Die Zeile enthält keine Werte.",
        );
      case "noReadings":
        return t(
          "energyReport.issues.noReadings",
          "Das Fahrzeug hat im Zeitraum keinen Verbrauchszählerstand gemeldet. Der Verbrauch ist unbekannt, nicht null.",
        );
      case "singleReading":
        return t(
          "energyReport.issues.singleReading",
          "Es liegt nur ein einzelner Zählerstand vor. Daraus lässt sich kein Verbrauch berechnen.",
        );
      case "partialCoverage":
        return t(
          "energyReport.issues.partialCoverage",
          "Zählerstände nur von {{from}} bis {{to}}. {{km}} km wurden außerhalb dieses Zeitraums gefahren und sind im Verbrauch nicht enthalten.",
          {
            from: formatDate(row.coverageFrom ?? undefined, language),
            to: formatDate(row.coverageTo ?? undefined, language),
            km: formatNumber(row.uncoveredDistanceKm, language),
          },
        );
      case "counterReset":
        return t(
          "energyReport.issues.counterReset",
          "Der Zähler ist im Zeitraum {{count}}-mal zurückgesprungen (Gerätetausch oder fehlerhafter Messwert). Der Verbrauch wurde je Anstieg summiert.",
          { count: row.resetCount },
        );
      case "noDistance":
        return t(
          "energyReport.issues.noDistance",
          "Für den Messzeitraum liegen keine Fahrten vor. Ein Verbrauch je 100 km lässt sich nicht berechnen.",
        );
      case "truncated":
        return t(
          "energyReport.issues.truncated",
          "Geotab hat die maximale Anzahl Datensätze geliefert. Es können weitere fehlen.",
        );
    }
  });
}

/**
 * The plausibility hints for a row — separate from the completeness issues above, because a
 * figure can be complete and still worth a second look.
 *
 * @param row - The row to describe.
 * @param t - The translation function.
 * @param language - The language to format numbers in.
 * @returns One sentence per hint.
 */
export function energyHintTexts(row: ConsumptionRow, t: Translate, language: string): string[] {
  const hints: string[] = [];

  if (row.fuelDeviationFlagged) {
    hints.push(
      t(
        "energyReport.hints.fuelDeviation",
        "Es wurden {{liters}} l mehr getankt als der Zähler verbraucht hat. Mögliche Ursachen: fehlerhafter Messwert am Fahrzeug oder Kraftstoffverlust.",
        { liters: formatNumber(row.fuelDeviationLiters, language, { precision: 2 }) },
      ),
    );
  }
  if (row.fillVolumeMissing) {
    hints.push(
      t(
        "energyReport.hints.fillVolumeMissing",
        "Mindestens ein erkannter Tankvorgang ohne ermittelbare Menge. Die getankte Summe ist unvollständig.",
      ),
    );
  }
  if (row.noFillsRecorded) {
    hints.push(
      t(
        "energyReport.hints.noFillsRecorded",
        "Kraftstoff verbraucht, aber im Zeitraum kein Tankvorgang erfasst.",
      ),
    );
  }

  return hints;
}
